import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { useCheckPartnerCustomer } from "@/apps/partner/hooks/useCheckPartnerCustomer";
import type { AddDeviceFormType } from "@/apps/partner/types";
import { Info } from "@/assets/icons/icons";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { Step } from "@/components/Onboarding/Steps/Step";
import { BACKEND_EMAIL_REGEX } from "@/utils/constants";

import { PersonalDetailsFormFields } from "./PersonalDetailsFormFields";

/**
 * This component handles the personal details step in the onboarding process.
 * It manages form state for both private and business customers, handles
 * form submission for new and existing users, and navigates to the next step
 * upon successful submission.
 *
 * Key features:
 * - Checks if customer already exists by email (CSC check)
 * - Hides personal details form when customer exists
 * - Switches between private and business customer forms
 * - Handles form state and validation
 */
export const PersonalDetails: FCC = () => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    setValue,
    unregister,
    formState: { errors },
  } = useFormContext<AddDeviceFormType>();

  const emailValue = watch("mail") || "";
  const { isExistingCustomer, isChecking } =
    useCheckPartnerCustomer(emailValue);

  // Sync the isExistingCustomer flag into the form data
  // and unregister customer fields when customer exists to skip validation
  useEffect(() => {
    setValue("isExistingCustomer", isExistingCustomer);

    if (isExistingCustomer) {
      unregister("customer");
    }
  }, [isExistingCustomer, setValue, unregister]);

  return (
    <Step id="partner-personal-details">
      <div className="w-full">
        <div className="flex flex-col gap-4 md:flex-row">
          <div id="personal-details-form-fields" className="w-full">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="flex flex-col gap-4">
                <h2>{t("partnerPortal:addDevice.step4.title")}</h2>
                <p className="text-black">
                  {t("partnerPortal:addDevice.step4.description")}
                </p>
              </div>
              <div className="hidden border-[1px] border-secondary-100 md:block" />
              <div className="flex flex-col justify-center gap-4">
                <h2 className="font-semibold text-secondary-100">
                  {t("partnerPortal:addDevice.step4.hintTitle")}
                </h2>
                <p className="text-black">
                  {t("partnerPortal:addDevice.step4.hint")}
                </p>
              </div>
            </div>
            <p className="text-xs text-black">
              * {t("Onboarding.sections.personal-details.form.requiredInfo")}
            </p>

            <div className="flex w-full flex-col md:w-1/2">
              <FormField
                id="new-email-input"
                label={t("user-account.personalDetails.loginData.email")}
                autocomplete="email"
                name="mail"
                errors={errors}
                register={register}
                type="email"
                className="md:pr-2.5"
                placeholder={t(
                  "translation:Common.generalForm.fields.email.placeholder",
                )}
                options={{
                  required: t("translation:label.validation.required"),
                  pattern: {
                    value: BACKEND_EMAIL_REGEX,
                    message: t(
                      "user-account.personalDetails.loginData.currentEmailError",
                    ),
                  },
                  maxLength: 50,
                  invalidEmailError: t(
                    "user-account.personalDetails.loginData.currentEmailError",
                  ),
                }}
              />

              {isChecking && (
                <p className="mt-1 text-sm text-primary-100">
                  {t("partnerPortal:addDevice.step4.checkingCustomer")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {isExistingCustomer ? (
        <div className="bg-highlight-100/10 mt-6 flex items-center gap-3 rounded-lg border border-highlight-100 p-4">
          <Info className="size-6 flex-shrink-0 text-primary-100" />
          <p className="text-base text-primary-100">
            {t("partnerPortal:addDevice.step4.existingCustomer")}
          </p>
        </div>
      ) : (
        <PersonalDetailsFormFields />
      )}
    </Step>
  );
};
